import { Paperclip } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardBody } from '@vnetwork/ui/primitives';
import type { TicketDetail } from '@/api/support';
import { formatDateTime } from '../shared';

/** Only allow http(s) or root-relative attachment links — blocks javascript:/data: hrefs. */
function safeHref(path: string): string | null {
  if (path.startsWith('/')) return path;
  try {
    const url = new URL(path);
    return url.protocol === 'http:' || url.protocol === 'https:' ? path : null;
  } catch {
    return null;
  }
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        padding: '10px 0',
        borderBottom: '1px solid var(--vn-border-subtle, var(--vn-border-default))',
      }}
    >
      <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
        {label}
      </span>
      <span style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 500 }}>{children}</span>
    </div>
  );
}

const muted = (text = '—') => <span style={{ color: 'var(--vn-fg-muted)' }}>{text}</span>;

/** "" → em dash; a date string → formatted date-time. */
function dateOrDash(iso: string): React.ReactNode {
  return iso ? formatDateTime(iso) : muted();
}

export function TicketMetaRail({ ticket }: { ticket: TicketDetail }) {
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Card>
        <CardHeader>
          <CardTitle>Timeline</CardTitle>
        </CardHeader>
        <CardBody style={{ paddingTop: 0, paddingBottom: 4 }}>
          <DetailRow label="Requested">
            {formatDateTime(ticket.ticket_requested_at || ticket.created_at)}
          </DetailRow>
          <DetailRow label="First response">{dateOrDash(ticket.first_response_at)}</DetailRow>
          <DetailRow label="Last reply">{dateOrDash(ticket.last_reply_at)}</DetailRow>
          <DetailRow label="Expected completion">{dateOrDash(ticket.due_at)}</DetailRow>
          <DetailRow label="Resolved">{dateOrDash(ticket.ticket_resolved_at)}</DetailRow>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>People</CardTitle>
        </CardHeader>
        <CardBody style={{ paddingTop: 0, paddingBottom: 4 }}>
          <DetailRow label="Technical owner">
            {ticket.assignee_name || muted('Unassigned')}
          </DetailRow>
          <DetailRow label="Created by">{ticket.created_by_name || muted()}</DetailRow>
        </CardBody>
      </Card>

      {ticket.attachments.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Attachments ({ticket.attachments.length})</CardTitle>
          </CardHeader>
          <CardBody>
            <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              {ticket.attachments.map((a) => {
                const href = safeHref(a.path);
                const inner = (
                  <>
                    <Paperclip size={14} color="var(--vn-fg-muted)" />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {a.name}
                    </span>
                  </>
                );
                const style: React.CSSProperties = {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--vn-space-2)',
                  fontSize: 'var(--vn-font-size-sm)',
                  color: 'var(--vn-fg-default)',
                  textDecoration: 'none',
                };
                return href ? (
                  <a key={a.attachment_id} href={href} target="_blank" rel="noreferrer" style={style}>
                    {inner}
                  </a>
                ) : (
                  <span key={a.attachment_id} style={style}>
                    {inner}
                  </span>
                );
              })}
            </div>
          </CardBody>
        </Card>
      ) : null}

      {ticket.history.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>History</CardTitle>
          </CardHeader>
          <CardBody>
            <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid' }}>
              {ticket.history.map((h, i) => {
                const last = i === ticket.history.length - 1;
                return (
                  <li key={`${h.action}-${h.created_at}-${i}`} style={{ display: 'flex', gap: 'var(--vn-space-3)' }}>
                    {/* dot + connector */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <span
                        style={{
                          width: 9,
                          height: 9,
                          borderRadius: '50%',
                          background: 'var(--vn-primary-solid)',
                          marginTop: 4,
                          flexShrink: 0,
                        }}
                      />
                      {!last ? (
                        <span style={{ flex: 1, width: 1, background: 'var(--vn-border-default)' }} />
                      ) : null}
                    </div>
                    <div style={{ paddingBottom: last ? 0 : 16, minWidth: 0 }}>
                      <div style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 500 }}>
                        {h.label || h.action}
                      </div>
                      <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
                        {formatDateTime(h.created_at)}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}
